import { useEffect, useRef, useState } from 'react'
import { useDesktopStore } from '@/stores/desktopStore'
import { gsap } from 'gsap'
import './Preloader.css'

// Terminal lines configuration
const TERMINAL_LINES = [
  { type: 'highlight', text: 'Dimensional Coordinates: Alpha/Omega/Prime', top: 0 },
  { type: 'faded', text: 'Quantum Fluctuation Nominal', top: 25 },
  { type: 'highlight', text: 'Initiate Quantum Calibration', top: 50 },
  { type: 'faded', text: 'Initiating Spacetime Fold', top: 75 },
  { type: 'highlight', text: 'Beginning Tesseract Unfolding', top: 100 },
  { type: 'faded', text: 'Scanning Parallel Realities', top: 125 },
  { type: 'highlight', text: 'Hyperdimensional Matrices Aligned', top: 150 },
  { type: 'highlight', text: 'Quantum Entanglement Stabilized', top: 195 },
  { type: 'faded', text: 'Processing Gravitational Waves', top: 220 },
  { type: 'highlight', text: 'Cosmic Strings Vibrating in Harmony', top: 245 },
  { type: 'faded', text: 'Calibrating Temporal Displacement', top: 270 },
  { type: 'highlight', text: 'Wormhole Aperture Expanding', top: 295 },
]

const SCRAMBLE_CHARS = '▪▫◾◽■□▢▣'

export function Preloader() {
  const { setLoading, setDesktopReady } = useDesktopStore()
  const [progress, setProgress] = useState(0)
  const [isExiting, setIsExiting] = useState(false)
  const [displayTexts, setDisplayTexts] = useState<string[]>(TERMINAL_LINES.map(() => ''))
  const containerRef = useRef<HTMLDivElement>(null)
  const linesRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const totalDuration = 5000 // 5 seconds
    const startTime = performance.now()

    // Scramble text effect
    const scrambleText = (original: string, progress: number): string => {
      const chars = original.split('')
      const revealedCount = Math.floor(chars.length * progress)
      
      return chars.map((char, i) => {
        if (i < revealedCount) return char
        if (char === ' ') return ' '
        return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)]
      }).join('')
    }

    // Animate terminal lines
    const animateLines = () => {
      const elapsed = performance.now() - startTime
      const overallProgress = Math.min(elapsed / totalDuration, 1)
      
      setProgress(Math.floor(overallProgress * 100))

      // Update each line with scramble effect
      const newTexts = TERMINAL_LINES.map((line, index) => {
        const lineDelay = index * 0.05
        const lineProgress = Math.max(0, Math.min(1, (overallProgress - lineDelay) / 0.15))
        return scrambleText(line.text, lineProgress)
      })
      setDisplayTexts(newTexts)

      // Show lines progressively
      linesRef.current.forEach((el, index) => {
        if (el) {
          const lineProgress = Math.max(0, Math.min(1, (overallProgress * 1.2) - (index * 0.05)))
          el.style.opacity = String(lineProgress)
        }
      })

      if (overallProgress < 1) {
        requestAnimationFrame(animateLines)
      } else {
        // Complete - trigger exit animation
        setTimeout(() => {
          setIsExiting(true)
          
          // Animate lines disappearing
          gsap.to(linesRef.current, {
            opacity: 0,
            y: -10,
            stagger: 0.03,
            duration: 0.3,
            ease: 'power2.in',
          })

          setTimeout(() => {
            setLoading(false)
            setDesktopReady(true)
          }, 800)
        }, 300)
      }
    }

    requestAnimationFrame(animateLines)
  }, [setLoading, setDesktopReady])

  return (
    <div 
      className={`preloader ${isExiting ? 'preloader--exiting' : ''}`}
      ref={containerRef}
    >
      <div className="preloader__terminal">
        {/* Header */}
        <div className="preloader__terminal-header">
          <span>Dimensional Gateway</span>
          <span>Traversal Initiated</span>
        </div>

        {/* Terminal lines container */}
        <div className="preloader__terminal-content">
          {TERMINAL_LINES.map((line, index) => (
            <div
              key={index}
              ref={(el) => { linesRef.current[index] = el }}
              className={`preloader__terminal-line preloader__terminal-line--${line.type}`}
              style={{ top: `${line.top}px`, opacity: 0 }}
            >
              {displayTexts[index]}
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="preloader__progress-section">
          <span className="preloader__progress-label">TRAVERSING</span>
          <div className="preloader__progress-bar">
            <div 
              className="preloader__progress-fill" 
              style={{ width: `${progress}%` }} 
            />
          </div>
          <span className="preloader__progress-text">DIMENSIONAL SHIFT</span>
        </div>

        {/* Footer */}
        <div className="preloader__terminal-footer">
          <span>Traversal Sequence {progress < 100 ? 'In Progress' : 'Complete'}</span>
          <span>Dimensional Gateway {progress < 100 ? 'Opening' : 'Open'}</span>
        </div>
      </div>
    </div>
  )
}
